/**
 * Tipli API istemcisi.
 *
 * Yollar, parametreler ve yanıt gövdeleri `schema.d.ts`'ten gelir; o dosya
 * OpenAPI şemasından üretilir. Uçta bir alan değişince panelde derleme hatası
 * çıkar — çalışma zamanında fark edilen sessiz bir bozulma yerine (YES-78).
 *
 * El yazımı `fetch` yazmayın: tip güvenliği oradan kaçar.
 */
import createClient, { type Middleware } from 'openapi-fetch'
import type { paths } from './schema'
import { toApiError } from './errors'

export type ClientOptions = {
  /** Varsayılan: aynı origin. Panel Vite vekili üzerinden `/api`'ye gider. */
  baseUrl?: string
  /**
   * Hangi dernek adına istek yapıldığı. Kullanıcı birden fazla derneğe
   * bağlıysa seçtiği dernek buraya yazılır; alt alan adı kullanılıyorsa
   * gerekmez.
   */
  getTenantSlug?: () => string | null
  /** Kısa ömürlü erişim jetonu. Bellekte tutulur, depolamaya yazılmaz. */
  getAccessToken?: () => string | null
  /** Oturum yenileme ya da çıkış için: 401 alındığında çağrılır. */
  onUnauthenticated?: () => void
}

export const TENANT_HEADER = 'X-Tenant-Slug'

/**
 * Hata ara katmanı: problem+json gövdesini `ApiError`'a çevirir ve fırlatır.
 * Böylece çağıran her yerde `error.is('quota_exceeded')` yazabilir.
 */
function errorMiddleware(options: ClientOptions): Middleware {
  return {
    async onRequest({ request }) {
      const token = options.getAccessToken?.()
      if (token) request.headers.set('Authorization', `Bearer ${token}`)
      const slug = options.getTenantSlug?.()
      if (slug) request.headers.set(TENANT_HEADER, slug)
      return request
    },
    async onResponse({ response }) {
      if (response.ok) return response

      const clone = response.clone()
      let body: unknown = null
      try {
        body = await clone.json()
      } catch {
        body = await clone.text().catch(() => null)
      }

      if (response.status === 401) options.onUnauthenticated?.()
      throw toApiError(response.status, body)
    },
  }
}

export function createApiClient(options: ClientOptions = {}) {
  const client = createClient<paths>({
    baseUrl: options.baseUrl ?? '',
    // Refresh token httpOnly çerezde taşınıyor (YES-83); çerez gönderilmeli.
    credentials: 'include',
  })
  client.use(errorMiddleware(options))
  return client
}

export type ApiClient = ReturnType<typeof createApiClient>
